'use client'

import { isButtonVariant } from '@syconium/little-miss-figgy/dist/components/Button/utils'
import { MediaAsContents } from '@syconium/little-miss-figgy/dist/components/Media'
import { mdEndPixels, pageLayoutMaxWidthPixels } from '@syconium/little-miss-figgy/dist/constants'
import { isUppercase } from '@syconium/little-miss-figgy/dist/lib/utils/isUppercase'

import { gql } from '../../../../../__generated__/graphql/catalog/gql'
import { PageHeroSectionFragmentFragment } from '../../../../../__generated__/graphql/catalog/graphql'
import { trackEvent } from '../../../../../lib/analytics'
import { useTranslation } from '../../../../_providers/TranslationProvider.client'
import { ClientOnlyResponsiveVideo } from '../../../media/ClientOnlyResponsiveVideo.client'
import { WatchVideoDialog } from '../../../media/WatchVideoDialog.client'
import { NextLink } from '../../../navigation/NextLink'
import { ContentfulRichText } from '../components/ContentfulRichText'
import { getContentfulPageSectionAnalyticsName } from '../ContentfulPageSectionProvider.utils'

import {
  Container,
  CtaButton,
  Disclaimer,
  Eyebrow,
  HeroBottomMargin,
  HeroImage,
  ImageWrap,
  ImageWrapOverlay,
  StyledButtonBar,
  StyledButtonList,
  StyledButtonListItem,
  StyledScrollDownArrow,
  Subtitle,
  TextBelowSection,
  TextWrap,
  TextWrapTitleImage,
  Title,
  TopArrowSpacer,
  VideoModalButtonWrapper,
} from './ContentfulPageHeroSection.styles'

export const pageHeroSectionFragment = gql(`
  fragment PageHeroSectionFragment on PageHeroSection {
    __typename  
    id
    backgroundColor
    overlayColor
    buttonVariantDesktop
    buttonVariantMobile
    disclaimer
    description
    eyebrow
    hideDownArrow
    imageDesktop
    imageMobile
    inlineVideoDesktop
    inlineVideoMobile
    inlineVimeoIdDesktop
    inlineVimeoIdMobile
    isTitleSerif
    isTitleSerif
    isTitleItalic
    linkHref1
    linkHref2
    linkText1
    linkText2
    modalVideoId
    modalVideoIdMobile
    modalVideoPlatform
    modalVideoPlatformMobile
    openVideoModalText
    subtitle
    mobileTitleColor
    desktopTitleColor
    textContentMaxWidthDesktop
    textContentMaxWidthMobile
    textPlacementDesktop
    textPlacementMobile
    title
    titleImage
    titleImageAspectRatio
    titleImageMaxWidthDesktop
    titleImageMaxWidthMobile
    titleImageMobile
    titleImageMobileAspectRatio
    isFullscreenHeight
    hasVerticalMargin
    name
  }
`)

export type ContentfulPageHeroSectionProps = {
  pageSectionIndex: number
  data: PageHeroSectionFragmentFragment
  isCriticalImage?: boolean
}

export const ContentfulPageHeroSection = ({
  pageSectionIndex,
  isCriticalImage = pageSectionIndex === 0,
  data,
}: ContentfulPageHeroSectionProps) => {
  const translations = useTranslation()
  const section = data

  const category = getContentfulPageSectionAnalyticsName(section.__typename)

  const {
    backgroundColor,
    buttonVariantDesktop,
    buttonVariantMobile,
    disclaimer,
    description,
    eyebrow,
    hideDownArrow,
    imageDesktop,
    imageMobile,
    inlineVideoDesktop,
    inlineVideoMobile,
    inlineVimeoIdDesktop,
    inlineVimeoIdMobile,
    isTitleItalic,
    isTitleSerif,
    linkHref1,
    linkHref2,
    linkText1,
    linkText2,
    modalVideoId,
    modalVideoIdMobile,
    modalVideoPlatform,
    modalVideoPlatformMobile,
    name,
    overlayColor,
    subtitle,
    mobileTitleColor,
    desktopTitleColor,
    textContentMaxWidthDesktop,
    textContentMaxWidthMobile,
    textPlacementDesktop,
    textPlacementMobile,
    title: propsTitle,
    titleImage,
    titleImageAspectRatio,
    titleImageMaxWidthDesktop,
    titleImageMaxWidthMobile,
    titleImageMobile,
    titleImageMobileAspectRatio,
    isFullscreenHeight,
    hasVerticalMargin,
    openVideoModalText,
  } = section

  const modalVideo =
    modalVideoId && modalVideoPlatform
      ? { id: modalVideoId, platform: modalVideoPlatform }
      : undefined
  const modalVideoMobile =
    modalVideoIdMobile && modalVideoPlatformMobile
      ? { id: modalVideoIdMobile, platform: modalVideoPlatformMobile }
      : undefined
  const defaultButtonVariant = 'black-on-white-no-border'
  const title = propsTitle
  const computedButtonVariantMobile = isButtonVariant(buttonVariantMobile)
    ? buttonVariantMobile
    : defaultButtonVariant
  const computedButtonVariantDesktop = isButtonVariant(buttonVariantDesktop)
    ? buttonVariantDesktop
    : defaultButtonVariant

  const pageSectionDisplayName = title ?? name ?? undefined
  const pageSectionContentfulName = name ?? undefined
  const analyticsAssetSrc = titleImage ?? imageDesktop ?? imageMobile ?? undefined

  const DisclaimerText = (
    <Disclaimer
      $textColorMobile={mobileTitleColor}
      $textColorDesktop={desktopTitleColor}
      $placement={textPlacementDesktop}
    >
      <ContentfulRichText content={disclaimer} bodySize='BodyTiny' fontWeight='600' />
    </Disclaimer>
  )

  const HeroText = (
    <TextWrap
      $mobileTextColor={mobileTitleColor}
      $desktopTextColor={desktopTitleColor}
      $textPlacementMobile={textPlacementMobile}
      $textPlacementDesktop={textPlacementDesktop}
      $textContentMaxWidthDesktop={textContentMaxWidthDesktop}
      $textContentMaxWidthMobile={textContentMaxWidthMobile}
      $hasDisclaimer={!!disclaimer}
    >
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      {titleImage && (
        <TextWrapTitleImage
          $titleImageMaxWidthDesktop={titleImageMaxWidthDesktop}
          $titleImageMaxWidthMobile={titleImageMaxWidthMobile}
          alt={(title || description) ?? ''}
          src={titleImage}
          srcs={
            titleImageMobile
              ? {
                  sm: titleImageMobile,
                  md: titleImage,
                }
              : undefined
          }
          loading={isCriticalImage ? 'default' : 'lazy'}
          aspectRatios={{
            sm: titleImageMobile ? titleImageMobileAspectRatio : titleImageAspectRatio,
            md: titleImageAspectRatio,
          }}
          widths={{
            unit: 'px',
            sm: titleImageMaxWidthMobile ?? mdEndPixels,
            md: titleImageMaxWidthDesktop ?? pageLayoutMaxWidthPixels,
          }}
          transparentBackground
        />
      )}
      {title && (
        <Title
          {...{ $isTitleSerif: isTitleSerif, $isTitleItalic: isTitleItalic }}
          asTag='h3'
          isUppercase={isUppercase(title)}
        >
          {title}
        </Title>
      )}
      {subtitle && <Subtitle isUppercase={isUppercase(subtitle)}>{subtitle}</Subtitle>}
      <MediaAsContents lessThan='md'>
        {!!modalVideoMobile && (
          <VideoModalButtonWrapper>
            <WatchVideoDialog
              videoId={modalVideoMobile.id}
              videoPlatform={modalVideoMobile.platform}
              text={openVideoModalText}
            />
          </VideoModalButtonWrapper>
        )}
      </MediaAsContents>
      <MediaAsContents greaterThanOrEqual='md'>
        {!!modalVideo && (
          <VideoModalButtonWrapper>
            <WatchVideoDialog
              videoId={modalVideo.id}
              videoPlatform={modalVideo.platform}
              text={openVideoModalText}
            />
          </VideoModalButtonWrapper>
        )}
      </MediaAsContents>
      <StyledButtonBar>
        <StyledButtonList role='list'>
          {linkText1 && linkHref1 && (
            <StyledButtonListItem role='listitem'>
              <CtaButton
                as={NextLink}
                href={linkHref1}
                variantDesktop={computedButtonVariantDesktop}
                variantMobile={computedButtonVariantMobile}
                {...trackEvent({
                  category,
                  action: 'click cta 1',
                  label: linkText1,
                  pageSectionName: pageSectionDisplayName,
                  contentfulName: pageSectionContentfulName,
                  pageSectionIndex,
                  correspondingAsset: analyticsAssetSrc,
                })}
              >
                {linkText1}
              </CtaButton>
            </StyledButtonListItem>
          )}
          {linkText2 && linkHref2 && (
            <StyledButtonListItem role='listitem'>
              <CtaButton
                as={NextLink}
                href={linkHref2}
                variantMobile={computedButtonVariantMobile}
                variantDesktop={computedButtonVariantDesktop}
                {...trackEvent({
                  category,
                  action: 'click cta 2',
                  label: linkText2,
                  pageSectionName: pageSectionDisplayName,
                  contentfulName: pageSectionContentfulName,
                  pageSectionIndex,
                  correspondingAsset: analyticsAssetSrc,
                })}
              >
                {linkText2}
              </CtaButton>
            </StyledButtonListItem>
          )}
        </StyledButtonList>
      </StyledButtonBar>
      {textPlacementDesktop !== 'center' && DisclaimerText}
    </TextWrap>
  )

  return (
    <>
      <Container
        $isFullscreenHeight={isFullscreenHeight}
        $textPlacementMobile={textPlacementMobile}
        $data-testid='page-hero-section-container'
      >
        {hideDownArrow !== true ? <TopArrowSpacer /> : null}
        <ImageWrap
          $backgroundColor={backgroundColor}
          $textPlacementMobile={textPlacementMobile}
          $shouldCollapse={!(imageMobile || inlineVideoMobile || !!modalVideo)}
        >
          <HeroImage
            imageDesktop={imageDesktop}
            imageMobile={imageMobile}
            alt={title}
            isCriticalImage={isCriticalImage}
          />
          <ClientOnlyResponsiveVideo
            inlineVideoDesktop={inlineVideoDesktop}
            inlineVideoMobile={inlineVideoMobile}
            inlineVimeoIdDesktop={inlineVimeoIdDesktop}
            inlineVimeoIdMobile={inlineVimeoIdMobile}
            playText={translations.magnolia.general.playVideo}
            pauseText={translations.magnolia.general.pauseVideo}
            objectPosition='top'
          />
          <ImageWrapOverlay overlayColor={overlayColor} />
        </ImageWrap>

        <MediaAsContents greaterThanOrEqual={textPlacementMobile === 'below' ? 'md' : 'xs'}>
          {HeroText}
        </MediaAsContents>

        {!hideDownArrow ? (
          <>
            <MediaAsContents lessThan='md'>
              <StyledScrollDownArrow
                $hideDownArrow={hideDownArrow}
                $textColor={mobileTitleColor}
                $textPlacementMobile={textPlacementMobile}
              />
            </MediaAsContents>

            <MediaAsContents greaterThanOrEqual='md'>
              <StyledScrollDownArrow
                $hideDownArrow={hideDownArrow}
                $textColor={desktopTitleColor}
                $textPlacementMobile={textPlacementMobile}
              />
            </MediaAsContents>
          </>
        ) : null}
        {textPlacementDesktop === 'center' && DisclaimerText}
      </Container>

      {textPlacementMobile === 'below' ? (
        <MediaAsContents lessThan='md'>
          <TextBelowSection>{HeroText}</TextBelowSection>
        </MediaAsContents>
      ) : null}
      {hasVerticalMargin && <HeroBottomMargin />}
    </>
  )
}
